import { Search } from '@mui/icons-material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArticleIcon from '@mui/icons-material/Article';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPreviewText } from '../../../../lib/utils';
import { fetchContext, getProjectIssues } from '../../../../modules/jira';
import LinkedProjectSelector from '../../../elements/common/traceability/LinkedProjectSelector';
import TestSuiteSelector from './TestSuiteSelector';
import { isAdminUser } from '../../../../lib/userUtils';

const BASE_FILTERS = `issuetype in ("Story")`;
const DEBOUNCE_MS = 400;
const DESCRIPTION_WORD_THRESHOLD = 30;

// ── Helpers ───────────────────────────────────────────────────────────────────

const buildJQL = (input, projectKey, linkedProjectId) => {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) return `project = ${linkedProjectId} AND ${BASE_FILTERS}`;
  if (/^\d+$/.test(trimmed)) return `project = ${linkedProjectId} AND key = "${projectKey}-${trimmed}" AND ${BASE_FILTERS}`;
  if (/^[A-Za-z][A-Za-z0-9]+-[0-9]+$/.test(trimmed)) return `project = ${linkedProjectId} AND key = "${trimmed}" AND ${BASE_FILTERS}`;
  return `project = ${linkedProjectId} AND summary ~ "${trimmed}*" AND ${BASE_FILTERS}`;
};

const transformIssues = (issues, jiraSite, projectId) =>
  issues.map((issue) => ({
    id: issue.id,
    key: issue.key,
    summary: issue.fields.summary,
    description: issue.fields.description ?? null,
    issueType: issue.fields.issuetype.name,
    issuestatus: issue.fields.status?.status_category?.name || issue.fields.status?.name || '',
    priority: issue.fields.priority?.name || '',
    siteUrl: jiraSite,
    projectId,
  }));

const extractAdfText = (node) => {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return ' ';
  return (Array.isArray(node.content) ? node.content : []).map(extractAdfText).join(' ');
};

const getDescriptionWordCount = (description) => {
  if (!description) return 0;
  const text = typeof description === 'object' ? extractAdfText(description) : description;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const isThinDescription = (description) => {
  if (!description) return false;
  return getDescriptionWordCount(description) < DESCRIPTION_WORD_THRESHOLD;
};

const resolvePageError = (error) => {
  if (error === 'inaccessible') return 'Cannot access this page (permission denied)';
  if (error === 'timeout') return 'Request timed out';
  if (error === 'no_content') return 'No readable content found';
  if (error === 'not_found') return 'Page not found';
  return 'Unavailable';
};
const ThinDescriptionWarning = ({ description }) => {
  const isEmpty = !description;

  const wordCount = getDescriptionWordCount(description);
  const missing = DESCRIPTION_WORD_THRESHOLD - wordCount;
  const pct = Math.min(100, Math.round((wordCount / DESCRIPTION_WORD_THRESHOLD) * 100));

  const whatsMissing = [];
  const text = typeof description === 'object' ? extractAdfText(description) : (description || '');
  const lower = text.toLowerCase();

  if (!/accept(ance criteria|ed when)|given |when |then /i.test(lower)) {
    whatsMissing.push('Acceptance criteria or Given/When/Then scenarios');
  }
  if (!/should|must|need|require|expect/i.test(lower)) {
    whatsMissing.push('Expected behaviour or requirements');
  }
  if (!/edge case|error|invalid|fail|negative/i.test(lower)) {
    whatsMissing.push('Edge cases or error conditions');
  }

  return (
    <Box sx={{ mt: 1, borderRadius: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <WarningAmberIcon
          sx={{ fontSize: 15, color: isEmpty ? 'error.main' : 'warning.main', flexShrink: 0 }}
        />
        <Typography variant="caption" fontWeight={700} color={isEmpty ? 'error.dark' : 'warning.dark'}>
          {isEmpty
            ? 'No description provided — cannot generate reliable test cases'
            : 'Description too short for reliable test generation'}
        </Typography>
      </Box>

      <Box sx={{ mb: 1 }}>
        {isEmpty ? null : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Word count: <strong>{wordCount}</strong> / {DESCRIPTION_WORD_THRESHOLD} minimum
              </Typography>
              <Typography variant="caption" color="warning.dark" fontWeight={600}>
                {missing} word{missing !== 1 ? 's' : ''} short
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 5,
                borderRadius: 3,
                bgcolor: '#FEF3C7',
                '& .MuiLinearProgress-bar': { bgcolor: '#F59E0B' },
              }}
            />
          </>
        )}
      </Box>
      {whatsMissing.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
            Consider adding:
          </Typography>
          {whatsMissing.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', mt: '6px', flexShrink: 0 }} />
              <Typography variant="caption">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        <AutoAwesomeIcon sx={{ fontSize: 10, mr: 0.5 }} />
        You can still proceed — AI will do its best with what's available.
      </Typography>
    </Box>
  );
};

const ContextSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
    <Skeleton variant="rounded" height={40} />
    <Skeleton variant="rounded" height={40} />
  </Box>
);

// ── ContextItem ───────────────────────────────────────────────────────────────

const ContextItem = ({ icon, label, excerpt, errorMsg, checked, accessible, onChange }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0.5, p: 1,
      bgcolor: 'background.paper', borderRadius: 1, border: '1px solid',
      borderColor: accessible ? 'primary.100' : 'divider',
      opacity: accessible ? 1 : 0.55,
    }}
  >
    <Checkbox
      size="small" checked={checked} disabled={!accessible}
      onChange={(e) => onChange(e.target.checked)}
      sx={{ p: 0, mr: 0.5, flexShrink: 0, mt: 0.15 }}
    />
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        <Typography
          variant="caption" fontWeight={600} lineHeight={1.4}
          color={accessible ? 'text.primary' : 'text.disabled'}
        >
          {label}
        </Typography>
      </Box>
      {excerpt && (
        <Typography
          variant="caption" color="text.secondary"
          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.25, lineHeight: 1.4 }}
        >
          {excerpt}
        </Typography>
      )}
      {errorMsg && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <WarningAmberIcon sx={{ fontSize: 12, color: 'warning.main' }} />
          <Typography variant="caption" color="warning.dark">{errorMsg}</Typography>
        </Box>
      )}
    </Box>
  </Box>
);

// ── ContextPanel ──────────────────────────────────────────────────────────────

const ContextPanel = ({ story, projectId, onContextChange }) => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [contextData, setContextData] = useState(null);
  const [includeEpic, setIncludeEpic] = useState(true);
  const [selectedPages, setSelectedPages] = useState(new Set());

  const onContextChangeRef = useRef(onContextChange);
  useEffect(() => { onContextChangeRef.current = onContextChange; }, [onContextChange]);

  const fetchedForKey = useRef(null);

  const emitContext = useCallback((data, epicOn, pages) => {
    if (!data) {
      onContextChangeRef.current({ epicContext: null, confluencePages: [] });
      return;
    }
    const epicContext = epicOn && data.epic
      ? { include: true, key: data.epic.key, summary: data.epic.summary, description: data.epic.description }
      : null;
    const confluencePages = (data.confluencePages || [])
      .filter((p) => pages.has(p.pageId) && !p.error)
      .map(({ pageId, title, url, excerpt }) => ({ pageId, title, url, excerpt }));
    onContextChangeRef.current({ epicContext, confluencePages });
  }, []);

  useEffect(() => {
    setEnabled(false);
    setOpen(true);
    setContextData(null);
    setSelectedPages(new Set());
    setIncludeEpic(true);
    fetchedForKey.current = null;
    onContextChangeRef.current({ epicContext: null, confluencePages: [] });
  }, [story?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !story?.key || !projectId) return;

    let cancelled = false;

    setLoading(true);
    setContextData(null);

    fetchContext(
      { storyKey: story.key, storyDescription: story.description || null, includeEpic: true, includeConfluence: true },
      projectId,
    )
      .then((data) => {
        if (cancelled) return;
        const normalized = {
          epic: data.epic,
          confluencePages: (data.confluence_pages || []).map((p) => ({
            pageId: p.page_id,
            title: p.title,
            url: p.url,
            excerpt: p.excerpt,
            error: p.error,
          })),
          confluenceError: data.confluence_error || null,
        };

        const defaultPages = new Set(
          normalized.confluencePages
            .filter((p) => !p.error)
            .map((p) => p.pageId),
        );

        setContextData(normalized);
        setSelectedPages(defaultPages);
        setIncludeEpic(true);
        emitContext(normalized, true, defaultPages);
      })
      .catch(() => {
        if (!cancelled) setContextData({ epic: null, confluencePages: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [enabled, story?.key, projectId, emitContext]);

  const handleEpicToggle = (checked) => {
    setIncludeEpic(checked);
    emitContext(contextData, checked, selectedPages);
  };

  const handlePageToggle = (pageId) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      next.has(pageId) ? next.delete(pageId) : next.add(pageId);
      return next;
    });
  };

  useEffect(() => {
    if (!contextData) return;
    emitContext(contextData, includeEpic, selectedPages);
  }, [selectedPages, contextData, includeEpic, emitContext]);

  const handleToggleEnabled = (checked) => {
    setEnabled(checked);
    if (!checked) onContextChangeRef.current({ epicContext: null, confluencePages: [] });
  };

  const hasEpic = !!contextData?.epic;
  const hasPages = (contextData?.confluencePages?.length ?? 0) > 0;
  const hasAnything = hasEpic || hasPages;
  const isEmpty = contextData !== null && !hasAnything;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'primary.200', borderRadius: 1.5, overflow: 'hidden', bgcolor: 'primary.50' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: 1, cursor: enabled ? 'pointer' : 'default', userSelect: 'none',
          '&:hover': { bgcolor: 'primary.100' },
        }}
        onClick={() => enabled && setOpen((v) => !v)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AutoAwesomeIcon sx={{ fontSize: 15, color: 'primary.main' }} />
          <Typography variant="caption" fontWeight={700} color="primary.main">
            Enhance with additional context
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={enabled ? 'Disable context enrichment' : 'Enable context enrichment (fetches Epic & Confluence docs)'}>
            <Switch size="small" checked={enabled} onChange={(e) => handleToggleEnabled(e.target.checked)} />
          </Tooltip>
          {enabled && (
            <IconButton size="small" sx={{ p: 0 }} onClick={() => setOpen((v) => !v)}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {enabled && (
        <Collapse in={open}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            {loading && <ContextSkeleton />}

            {!loading && isEmpty && (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                No additional context found for this story (no linked Epic or Confluence pages).
              </Typography>
            )}
            {contextData?.confluenceError && (
              <Alert
                severity={isAdminUser() ? 'error' : 'warning'}
                sx={{ py: 0, mr: 2 }}
              >
                {isAdminUser() ? (
                  <Box>
                    <Typography variant="caption" fontWeight={600} display="block">
                      Confluence access issue
                    </Typography>

                    <Typography variant="caption" display="block">
                      We couldn't load related Confluence pages due to missing permissions.
                    </Typography>

                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Fix this by reauthenticating Jira in project settings.
                    </Typography>

                    <Typography
                      component="a"
                      href={`/project/${projectId}/configurations?tab=jira`}
                      variant="caption"
                      sx={{
                        display: 'inline-block',
                        mt: 0.5,
                        fontWeight: 600,
                        color: 'error.main',
                        textDecoration: 'underline',
                      }}
                    >
                      Open settings →
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="caption" fontWeight={600} display="block">
                      Confluence content unavailable
                    </Typography>

                    <Typography variant="caption" display="block">
                      We couldn't load additional context for this story due to a permission issue.
                    </Typography>

                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Please contact your project admin to fix this.
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {!loading && hasAnything && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                {hasEpic && (
                  <ContextItem
                    icon={<AccountTreeIcon sx={{ fontSize: 14, color: 'secondary.main' }} />}
                    label={`Epic: ${contextData.epic.key} — ${contextData.epic.summary}`}
                    excerpt={contextData.epic.description}
                    checked={includeEpic}
                    accessible
                    onChange={handleEpicToggle}
                  />
                )}
                {(contextData.confluencePages || []).map((page) => {
                  const accessible = !page.error;
                  return (
                    <ContextItem
                      key={page.pageId}
                      icon={<ArticleIcon sx={{ fontSize: 14, color: accessible ? 'info.main' : 'text.disabled' }} />}
                      label={`Confluence: ${page.title}`}
                      excerpt={accessible ? page.excerpt?.slice(0, 120) : null}
                      errorMsg={!accessible ? resolvePageError(page.error) : null}
                      checked={accessible && selectedPages.has(page.pageId)}
                      accessible={accessible}
                      onChange={() => accessible && handlePageToggle(page.pageId)}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ── JiraStorySelector ─────────────────────────────────────────────────────────

const JiraStorySelector = ({
  currentProject,
  linkedJiraProjects,
  testSuites,
  selectedTestSuites,
  onTestSuitesChange,
  selectedStory,
  onStorySelect,
  generateError,
  onContextChange,
}) => {
  const { projectId } = useParams();
  const [selectedProject, setSelectedProject] = useState(linkedJiraProjects?.[0] || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');

  const currentProjectRef = useRef(currentProject);
  useEffect(() => { currentProjectRef.current = currentProject; }, [currentProject]);

  const debounceTimer = useRef(null);
  const abortCtrlRef = useRef(null);
  const hasLinkedProject = linkedJiraProjects?.length > 0;

  const handleSearch = useCallback(async (term, project) => {
    if (!project) return;

    abortCtrlRef.current?.abort();
    abortCtrlRef.current = new AbortController();
    const { signal } = abortCtrlRef.current;

    setIsLoading(true);
    setErr('');
    setIssues([]);

    try {
      const proj = currentProjectRef.current;
      const jql = buildJQL(term, project.linked_project_key, project.linked_project_id);
      const response = await getProjectIssues(
        { fields: ['summary', 'description', 'status', 'issuetype', 'priority'], jql },
        proj?.id,
        { signal },
      );

      if (signal.aborted) return;

      setIssues(
        Array.isArray(response?.issues)
          ? transformIssues(response.issues, proj?.jira_site_url, project.linked_project_id)
          : [],
      );
    } catch (error) {
      if (error?.name === 'AbortError') return;
      setErr('Error fetching issues. Please try again.');
      setIssues([]);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasLinkedProject || !selectedProject) return;
    handleSearch('', selectedProject);
  }, [selectedProject, handleSearch, hasLinkedProject]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceTimer.current);
      abortCtrlRef.current?.abort();
    };
  }, []);

  const scheduleDebouncedSearch = useCallback((term, project) => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => handleSearch(term, project), DEBOUNCE_MS);
  }, [handleSearch]);

  useEffect(() => {
    if (selectedStory === null) {
      setSearchTerm('');
      if (selectedProject) handleSearch('', selectedProject);
    }
  }, [selectedStory, selectedProject, handleSearch]);

  const handleProjectChange = (proj) => {
    setSelectedProject(proj);
    setIssues([]);
    onStorySelect(null);
    setSearchTerm('');
  };

  const hasNoDescription = selectedStory != null && !selectedStory.description;
  const showThinWarning = selectedStory != null && isThinDescription(selectedStory.description);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      {!hasLinkedProject ? (
        <Alert severity="warning">
          No Jira project is linked to this project. Please link a Jira project first.
        </Alert>
      ) : (
        <>
          {/* Search bar + project selector */}
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              clearTimeout(debounceTimer.current);
              handleSearch(searchTerm, selectedProject);
            }}
            sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Box sx={{ minWidth: 200 }}>
              <LinkedProjectSelector
                linkedProjects={linkedJiraProjects}
                onProjectChange={handleProjectChange}
                selectedProjectId={selectedProject?.linked_project_id}
              />
            </Box>
            <TextField
              placeholder="Search by key, ID, or summary"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                scheduleDebouncedSearch(e.target.value, selectedProject);
              }}
              fullWidth
              size="small"
              inputProps={{ 'aria-label': 'Search Jira issues' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="submit"
                      disabled={isLoading}
                      edge="end"
                      size="small"
                      aria-label="Run search"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      {isLoading ? <CircularProgress size={16} /> : <Search fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {err && <Alert severity="error" sx={{ py: 0 }}>{err}</Alert>}

          {/* Issue list */}
          <Box
            sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 1,
              maxHeight: 200, overflowY: 'auto', bgcolor: 'grey.50',
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : issues.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No stories found</Typography>
              </Box>
            ) : (
              issues.map((issue) => {
                const isSelected = selectedStory?.id === issue.id;
                return (
                  <Box
                    key={issue.id}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => onStorySelect(isSelected ? null : issue)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStorySelect(isSelected ? null : issue);
                      }
                    }}
                    sx={{
                      px: 2, py: 1.5, cursor: 'pointer',
                      borderBottom: '1px solid', borderBottomColor: 'divider',
                      bgcolor: isSelected ? 'primary.50' : 'transparent',
                      borderLeft: '3px solid',
                      borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                      outline: 'none',
                      '&:hover': { bgcolor: isSelected ? 'primary.50' : 'grey.100' },
                      '&:focus-visible': { boxShadow: 'inset 0 0 0 2px #3b82f6' },
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        component="a"
                        href={`${issue.siteUrl}/browse/${issue.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        fontWeight={600}
                        color="primary.main"
                      >
                        {issue.key}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 600 : 400}
                        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {issue.summary}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: '11px' }}>
                        {issue.issuestatus}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Story preview */}
          {selectedStory && (
            <Box
              sx={{
                p: 2, border: '1px solid', borderRadius: 1,
                borderColor: hasNoDescription ? 'error.main' : showThinWarning ? 'warning.main' : 'primary.200',
                bgcolor: hasNoDescription ? '#fff5f5' : showThinWarning ? '#fffbeb' : 'primary.50',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  component="a"
                  href={`${selectedStory.siteUrl}/browse/${selectedStory.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  fontWeight={600}
                  color="primary.main"
                >
                  {selectedStory.key}
                </Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{selectedStory.issueType}</Typography>
                {selectedStory.issuestatus && (
                  <>
                    <Typography variant="caption" color="text.secondary">·</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedStory.issuestatus}</Typography>
                  </>
                )}
              </Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{selectedStory.summary}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                {typeof selectedStory.description === 'object'
                  ? getPreviewText(selectedStory.description, 120)
                  : selectedStory.description}
              </Typography>
              {(hasNoDescription || showThinWarning) && (
                <ThinDescriptionWarning description={selectedStory.description} />
              )}
            </Box>
          )}

          {selectedStory && !hasNoDescription && (
            <ContextPanel key={selectedStory?.key} story={selectedStory} projectId={projectId} onContextChange={onContextChange} />
          )}

          <TestSuiteSelector
            testSuites={testSuites}
            selectedTestSuites={selectedTestSuites}
            onTestSuitesChange={onTestSuitesChange}
            projectId={projectId}
          />
          {generateError && <Alert severity="error" sx={{ py: 0 }}>{generateError}</Alert>}
        </>
      )}
    </Box>
  );
};

export default JiraStorySelector;